package com.merkost.handpass.admin.container

import com.merkost.handpass.admin.api.AdminRepositoryApi
import com.merkost.handpass.admin.contract.AsyncSection
import com.merkost.handpass.admin.contract.UserDetailAction
import com.merkost.handpass.admin.contract.UserDetailActionKind
import com.merkost.handpass.admin.contract.UserDetailIntent
import com.merkost.handpass.admin.contract.UserDetailState
import com.merkost.handpass.admin.contract.UserDetailTab
import com.merkost.handpass.admin.util.toAdminMessage
import com.merkost.handpass.api.dto.admin.AdminConversationSummary
import kotlinx.coroutines.MainScope
import pro.respawn.flowmvi.api.Container
import pro.respawn.flowmvi.api.PipelineContext
import pro.respawn.flowmvi.dsl.lazyStore
import pro.respawn.flowmvi.plugins.recover
import pro.respawn.flowmvi.plugins.reduce

class UserDetailContainer(private val repo: AdminRepositoryApi) : Container<UserDetailState, UserDetailIntent, UserDetailAction> {

    private val scope = MainScope()

    private var loadedUserId: String? = null

    override val store by lazyStore(UserDetailState.Loading, scope) {
        configure { name = "UserDetailStore" }
        recover { e ->
            updateState { UserDetailState.Error(e.toAdminMessage()) }
            null
        }
        reduce { intent ->
            when (intent) {
                is UserDetailIntent.Init -> {
                    if (loadedUserId != intent.userId) {
                        loadedUserId = intent.userId
                        load(intent.userId)
                        loadNotesAndAudit(intent.userId)
                        runCatching { repo.recordView("user", intent.userId) }
                    }
                }
                is UserDetailIntent.SetTab -> {
                    updateState {
                        if (this is UserDetailState.Content) copy(activeTab = intent.tab) else this
                    }
                    if (intent.tab == UserDetailTab.Conversations) {
                        val s = currentState() as? UserDetailState.Content
                        if (s != null && !s.conversationsRequested) loadConversations()
                    }
                }
                is UserDetailIntent.SetSuspendDays -> updateState {
                    if (this is UserDetailState.Content) copy(suspendDays = intent.days) else this
                }
                UserDetailIntent.TapWarn -> emitOpenReasonModal(UserDetailActionKind.Warn)
                UserDetailIntent.TapSuspend -> emitOpenReasonModal(UserDetailActionKind.Suspend)
                UserDetailIntent.TapBan -> emitOpenReasonModal(UserDetailActionKind.Ban)
                UserDetailIntent.TapRestore -> emitOpenReasonModal(UserDetailActionKind.Restore)
                is UserDetailIntent.ConfirmAction -> runConfirmedAction(intent.kind, intent.reason)
                is UserDetailIntent.UndoRestore -> undoRestore(intent.userId)
                UserDetailIntent.Refresh -> loadedUserId?.let { load(it) }
                is UserDetailIntent.SetNoteDraft -> updateState {
                    if (this is UserDetailState.Content) copy(newNoteDraft = intent.body) else this
                }
                is UserDetailIntent.SubmitNote -> {
                    val s = currentState() as? UserDetailState.Content ?: return@reduce
                    val userId = loadedUserId ?: return@reduce
                    repo.createNote("user", userId, s.newNoteDraft).onSuccess {
                        action(UserDetailAction.ShowSuccess("Note added"))
                        loadNotesAndAudit(userId)
                        updateState { if (this is UserDetailState.Content) copy(newNoteDraft = "") else this }
                    }.onFailure { e -> action(UserDetailAction.ShowError(e.toAdminMessage())) }
                }
                is UserDetailIntent.DeleteNote -> {
                    val userId = loadedUserId ?: return@reduce
                    repo.deleteNote(intent.noteId).onSuccess {
                        action(UserDetailAction.ShowSuccess("Note deleted"))
                        loadNotesAndAudit(userId)
                    }.onFailure { e -> action(UserDetailAction.ShowError(e.toAdminMessage())) }
                }
                UserDetailIntent.LoadConversations -> loadConversations()
                is UserDetailIntent.OpenConversation -> loadMessages(intent.conversation)
                UserDetailIntent.CloseConversation -> updateState {
                    if (this is UserDetailState.Content) copy(openConversation = null, messages = AsyncSection.Loading) else this
                }
            }
        }
    }

    private suspend fun PipelineContext<UserDetailState, UserDetailIntent, UserDetailAction>.loadConversations() {
        val userId = loadedUserId ?: return
        updateState {
            if (this is UserDetailState.Content) copy(conversations = AsyncSection.Loading, conversationsRequested = true) else this
        }
        val convos = repo.getUserConversations(userId)?.conversations
        updateState {
            if (this is UserDetailState.Content) copy(
                conversations = if (convos != null) AsyncSection.Loaded(convos) else AsyncSection.Failed("Failed to load conversations"),
            ) else this
        }
    }

    private suspend fun PipelineContext<UserDetailState, UserDetailIntent, UserDetailAction>.loadMessages(conversation: AdminConversationSummary) {
        updateState {
            if (this is UserDetailState.Content) copy(openConversation = conversation, messages = AsyncSection.Loading) else this
        }
        val msgs = repo.getConversationMessages(conversation.id)?.messages
        updateState {
            if (this is UserDetailState.Content) copy(
                messages = if (msgs != null) AsyncSection.Loaded(msgs) else AsyncSection.Failed("Failed to load messages"),
            ) else this
        }
    }

    private suspend fun PipelineContext<UserDetailState, UserDetailIntent, UserDetailAction>.loadNotesAndAudit(userId: String) {
        val notes = repo.listNotes("user", userId)?.notes
        val audit = repo.listAuditEntries("user", userId)?.entries
        updateState {
            if (this is UserDetailState.Content) copy(
                notes = if (notes != null) AsyncSection.Loaded(notes) else AsyncSection.Failed("Failed to load notes"),
                audit = if (audit != null) AsyncSection.Loaded(audit) else AsyncSection.Failed("Failed to load audit"),
            ) else this
        }
    }

    private suspend fun PipelineContext<UserDetailState, UserDetailIntent, UserDetailAction>.currentState(): UserDetailState {
        var s: UserDetailState = UserDetailState.Loading
        withState { s = this }
        return s
    }

    private suspend fun PipelineContext<UserDetailState, UserDetailIntent, UserDetailAction>.load(userId: String) {
        updateState { UserDetailState.Loading }
        val detail = repo.getUserDetail(userId)
        if (detail == null) {
            updateState { UserDetailState.Error("User not found") }
        } else {
            updateState { UserDetailState.Content(detail) }
        }
    }

    private suspend fun PipelineContext<UserDetailState, UserDetailIntent, UserDetailAction>.emitOpenReasonModal(kind: UserDetailActionKind) {
        val s = currentState()
        val targetName = (s as? UserDetailState.Content)?.detail?.profile?.fullName ?: "this user"
        val preview = if (kind == UserDetailActionKind.Ban) {
            loadedUserId?.let { id -> runCatching { repo.getBanPreview(id) }.getOrNull() }
        } else null
        action(UserDetailAction.OpenReasonModal(kind, targetName, preview))
    }

    private suspend fun PipelineContext<UserDetailState, UserDetailIntent, UserDetailAction>.runConfirmedAction(kind: UserDetailActionKind, reason: String?) {
        val userId = loadedUserId ?: return
        val s = currentState()
        val days = (s as? UserDetailState.Content)?.suspendDays ?: 7
        val result = when (kind) {
            UserDetailActionKind.Warn -> repo.warnUser(userId, reason)
            UserDetailActionKind.Suspend -> repo.suspendUser(userId, days, reason)
            UserDetailActionKind.Ban -> repo.banUser(userId, reason)
            UserDetailActionKind.Restore -> repo.restoreUser(userId, reason)
        }
        result.onSuccess {
            when (kind) {
                UserDetailActionKind.Suspend -> action(UserDetailAction.ShowUndoableSuccess("User suspended", userId))
                UserDetailActionKind.Ban -> action(UserDetailAction.ShowUndoableSuccess("User banned", userId))
                UserDetailActionKind.Warn -> action(UserDetailAction.ShowSuccess("User warned"))
                UserDetailActionKind.Restore -> action(UserDetailAction.ShowSuccess("User restored"))
            }
            load(userId)
        }
        result.onFailure { e ->
            action(UserDetailAction.ShowError(e.toAdminMessage()))
        }
    }

    private suspend fun PipelineContext<UserDetailState, UserDetailIntent, UserDetailAction>.undoRestore(userId: String) {
        repo.restoreUser(userId, "Reverted via undo")
            .onSuccess {
                action(UserDetailAction.ShowSuccess("User restored"))
                if (loadedUserId == userId) load(userId)
            }
            .onFailure { e -> action(UserDetailAction.ShowError(e.toAdminMessage())) }
    }
}
